/**
 * Modal for publishing a new version (internal users only).
 */

import { useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { publishVersion } from '@/api/versions';
import type { RequirementVersion, VersionCreateRequest } from '@/types';

interface PublishVersionModalProps {
  open: boolean;
  projectId: number;
  existingVersionNumbers: string[];
  onClose: () => void;
  onSuccess: (version?: RequirementVersion) => void;
}

const PublishVersionModal: React.FC<PublishVersionModalProps> = ({
  open,
  projectId,
  existingVersionNumbers,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: VersionCreateRequest = {
        version_number: values.version_number,
        version_info: values.version_info,
      };

      const version = await publishVersion(projectId, data);

      message.success('版本标签创建成功');
      form.resetFields();
      onSuccess(version);
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'isAxiosError' in err) {
        const axiosErr = err as unknown as { response?: { data?: { message?: string } }; message: string };
        const reason = axiosErr.response?.data?.message || axiosErr.message;
        message.error(`发布失败: ${reason}`);
      } else if (err instanceof Error) {
        // Validation error from form - don't show message
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="创建版本标签"
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="创建"
      cancelText="取消"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="版本号"
          name="version_number"
          rules={[
            { required: true, message: '请输入版本号' },
            {
              pattern: /^V\d+\.\d+$/,
              message: '版本号格式必须为 Vx.x（例如：V1.0）',
            },
            {
              validator: (_, value) => {
                if (value && existingVersionNumbers.includes(value)) {
                  return Promise.reject(new Error('版本号已存在'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="例如：V1.0" />
        </Form.Item>

        <Form.Item
          label="版本信息"
          name="version_info"
          rules={[
            { required: true, message: '请输入版本信息' },
            { max: 5000, message: '版本信息不能超过5000字' },
          ]}
        >
          <Input.TextArea
            rows={6}
            placeholder="请描述本次版本的主要内容和变更..."
            showCount
            maxLength={5000}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PublishVersionModal;
